import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import type { ECharts } from "echarts/core";
import { useDisplayTimezone } from "@nexus/ui";

/** Shared browser lifecycle. Each widget still owns its options, data and interactions. */
export function useChartLifecycle(
  hostRef: RefObject<HTMLElement | null>,
  chartRef: RefObject<ECharts | null>,
  onThemeChange: (host: HTMLElement, chart: ECharts) => void,
  lifecycleKey?: string,
) {
  const timezone = useDisplayTimezone();
  const previousTimezone = useRef(timezone);
  const redraw = useRef(onThemeChange);
  useLayoutEffect(() => {
    redraw.current = onThemeChange;
  }, [onThemeChange]);

  useEffect(() => {
    if (previousTimezone.current === timezone) return;
    previousTimezone.current = timezone;
    const host = hostRef.current;
    const chart = chartRef.current;
    if (host && chart && !chart.isDisposed()) {
      chart.setOption({ useUTC: timezone === "utc" });
      redraw.current(host, chart);
    }
  }, [timezone, lifecycleKey, hostRef, chartRef]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const themeObserver = new MutationObserver(() => {
      const chart = chartRef.current;
      if (chart && !chart.isDisposed()) redraw.current(host, chart);
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style", "data-density"],
    });

    let frame: number | undefined;
    // Chart labels are drawn in pixels: redraw when their role or the root size
    // changes (a presented page scales its root with the screen).
    const readLabelScale = () =>
      `${getComputedStyle(host).getPropertyValue("--widget-chart-label-size")}|${getComputedStyle(document.documentElement).fontSize}`;
    let labelScale = readLabelScale();
    const resizeObserver = new ResizeObserver(() => {
      // Grid resizing can report several changes in one frame.
      if (frame !== undefined) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = undefined;
        const chart = chartRef.current;
        if (chart && !chart.isDisposed()) {
          const nextScale = readLabelScale();
          if (nextScale !== labelScale) {
            labelScale = nextScale;
            redraw.current(host, chart);
          }
          // An observer reports the initial size too: resizing to the same size would
          // only cut the first draw's animation short (ECharts ends it on resize).
          if (host.clientWidth !== chart.getWidth() || host.clientHeight !== chart.getHeight())
            chart.resize();
        }
      });
    });
    resizeObserver.observe(host);

    return () => {
      themeObserver.disconnect();
      resizeObserver.disconnect();
      if (frame !== undefined) cancelAnimationFrame(frame);
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, [hostRef, chartRef, lifecycleKey]);
}
